// src/pages/documentation/gestion/GestionMarcasTiendas.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTags,
    faStore,
    faPlus,
    faEdit,
    faTrash,
    faImage,
    faGift,
    faCog,
    faPalette,
    faChartLine,
    faQrcode,
    faUsers,
    faUserPlus,
    faCreditCard,
    faStar,
    faGlobe,
    faCode,
    faArrowRight,
    faArrowLeft,
    faCheckCircle,
    faInfoCircle,
    faExclamationTriangle,
    faLightbulb
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const GestionMarcasTiendas = () => {
    const [expandedSections, setExpandedSections] = useState({
        introduccion: true,
        marcas: true,
        tiendas: true,
        herramientas: true
    });

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-blue-600 rounded-lg">
                            <FontAwesomeIcon icon={faTags} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Gestión de Marcas y Tiendas</h1>
                            <p className="text-gray-600">Aprende a administrar tus marcas y tiendas en PetitPrime</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/gestion" className="hover:text-gray-700">Gestión</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Marcas y Tiendas</span>
                    </div>
                </div>

                {/* Introducción */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué es la Gestión de Marcas y Tiendas?</h3>
                            <p className="text-gray-700 mb-3">
                                Esta sección te permite administrar la identidad de tu negocio (marcas) y todas tus ubicaciones físicas (tiendas). 
                                Desde aquí puedes configurar tu identidad visual, gestionar premios, configurar puntos, y administrar todas las 
                                herramientas de captación de clientes.
                            </p>
                            <div className="grid md:grid-cols-2 gap-4 mt-4">
                                <div className="bg-white rounded-lg p-4 border border-blue-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faTags} className="text-blue-600" />
                                        Marcas
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Tu marca es la identidad de tu negocio. Puedes tener múltiples marcas si gestionas varios negocios.
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-blue-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faStore} className="text-green-600" />
                                        Tiendas
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Las tiendas son tus ubicaciones físicas. Cada tienda puede tener su propia configuración y herramientas.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sección: Gestión de Marcas */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('marcas')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faTags} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Gestión de Marcas</h2>
                                <p className="text-sm text-gray-500">Crea y configura tus marcas</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.marcas ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.marcas && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Crear Marca */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faPlus} className="text-blue-600" />
                                        1. Crear una Marca
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">📍 ¿Dónde crear una marca?</h4>
                                            <p className="text-blue-700 text-sm mb-3">
                                                Ve a la página de <strong>"Gestión de Marcas y Tiendas"</strong> desde tu dashboard. 
                                                En la sección de "Mis Marcas", haz clic en el botón <strong>"Crear Marca"</strong>.
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-700">Información requerida:</h4>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Nombre de la marca:</strong> El nombre con el que se conocerá tu negocio
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Rubro:</strong> Selecciona el tipo de negocio (Restaurante, Tienda de Ropa, etc.)
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Condición para acumular puntos:</strong> Explica cómo los clientes ganan puntos (ej: "1 punto por cada euro gastado")
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Tipo de sistema:</strong> Elige entre "PUNTOS" o "ACUMULACIÓN"
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Puntos por ticket:</strong> Si eliges PUNTOS, define cuántos puntos se otorgan por cada compra
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Después de crear la marca, podrás agregar un logotipo. Es recomendable tenerlo listo antes de crear la marca.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Editar Marca */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faEdit} className="text-blue-600" />
                                        2. Editar una Marca
                                    </h3>
                                    <p className="text-gray-600 mb-4">
                                        Para editar una marca existente, haz clic en el icono de <strong>lápiz</strong> que aparece en la tarjeta de cada marca.
                                    </p>
                                    <p className="text-gray-600 text-sm">
                                        Puedes modificar todos los campos que configuraste al crear la marca, incluyendo nombre, rubro, condición de puntos, etc.
                                    </p>
                                </div>

                                {/* Logotipo */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faImage} className="text-blue-600" />
                                        3. Agregar o Cambiar Logotipo
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            El logotipo es la imagen que representa tu marca. Aparecerá en las tarjetas digitales de tus clientes y en todos los materiales de marketing.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo agregar un logotipo:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en el área del logotipo en la tarjeta de tu marca (o en "Clic para agregar" si no tienes logotipo)</li>
                                                <li>Selecciona una imagen desde tu computadora</li>
                                                <li>Formatos aceptados: JPG, PNG, GIF, WebP, SVG</li>
                                                <li>Tamaño máximo: 5MB</li>
                                                <li>El logotipo se actualizará automáticamente</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">✅ Recomendaciones:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Usa imágenes de alta calidad (mínimo 200x200 píxeles)</li>
                                                <li>• Preferiblemente formato PNG con fondo transparente</li>
                                                <li>• Asegúrate de que el logotipo sea legible en tamaños pequeños</li>
                                                <li>• El logotipo aparecerá en formato cuadrado, así que diseña pensando en ese formato</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Gestionar Premios */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faGift} className="text-blue-600" />
                                        4. Gestionar Premios (Solo para sistema de Acumulación)
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Si tu marca usa el sistema de <strong>"ACUMULACIÓN"</strong>, puedes configurar premios que los clientes pueden canjear con sus puntos acumulados.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo gestionar premios:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la tarjeta de tu marca, haz clic en <strong>"Gestionar Premios"</strong></li>
                                                <li>Agrega premios con su nombre, descripción y puntos requeridos</li>
                                                <li>Puedes editar o eliminar premios existentes</li>
                                                <li>Los premios aparecerán en las tarjetas digitales de tus clientes</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-yellow-700 text-sm">
                                                <strong>Nota:</strong> Esta opción solo está disponible para marcas con sistema de "ACUMULACIÓN". 
                                                Si tu marca usa "PUNTOS", los clientes acumulan puntos pero no hay premios predefinidos.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Configurar Puntos */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faCog} className="text-blue-600" />
                                        5. Configurar Motor de Puntos Automático
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            El motor automático de puntos te permite configurar reglas avanzadas para cómo los clientes acumulan puntos.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Tipos de configuración:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Tasa por cantidad:</strong> Define cuántos puntos se otorgan por cada cantidad de dinero gastado (ej: 1 punto por cada 5€)
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Sistema por niveles:</strong> Diferentes tasas de puntos según el monto de la compra
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Multiplicadores por categoría:</strong> Otorga más puntos para ciertas categorías de productos
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo configurar:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en <strong>"Configurar Puntos"</strong> en la tarjeta de tu marca</li>
                                                <li>Selecciona el modo de cálculo que prefieras</li>
                                                <li>Configura los parámetros según tu elección</li>
                                                <li>Guarda la configuración</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* Centro de Personalización */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faPalette} className="text-blue-600" />
                                        6. Centro de Personalización
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Personaliza completamente la identidad visual de tu marca: colores corporativos, tipografía y eslogan.
                                        </p>
                                        
                                        <div className="grid md:grid-cols-3 gap-4">
                                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                                <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                                                    <FontAwesomeIcon icon={faPalette} />
                                                    Colores
                                                </h4>
                                                <p className="text-purple-700 text-sm">
                                                    Define los colores corporativos de tu marca. Puedes agregar múltiples colores.
                                                </p>
                                            </div>
                                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                                <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                                                    <FontAwesomeIcon icon={faPalette} />
                                                    Tipografía
                                                </h4>
                                                <p className="text-purple-700 text-sm">
                                                    Selecciona la fuente que mejor represente tu marca. Hay más de 20 opciones disponibles.
                                                </p>
                                            </div>
                                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                                <h4 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                                                    <FontAwesomeIcon icon={faPalette} />
                                                    Eslogan
                                                </h4>
                                                <p className="text-purple-700 text-sm">
                                                    Agrega un eslogan o frase que identifique tu marca. Aparecerá en materiales de marketing.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo acceder:</h4>
                                            <p className="text-blue-700 text-sm">
                                                Haz clic en <strong>"Centro de Personalización"</strong> en la tarjeta de tu marca. 
                                                Desde allí podrás configurar todos los aspectos visuales de tu marca.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Gestionar Marketing */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChartLine} className="text-blue-600" />
                                        7. Gestionar Información de Marketing
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Configura tus redes sociales, información corporativa, palabras clave SEO y hashtags para mejorar tu presencia online.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Información que puedes agregar:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Redes sociales:</strong> Facebook, Instagram, Twitter/X, LinkedIn, TikTok, YouTube, WhatsApp Business
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Sitio web:</strong> URL de tu página web principal
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Información corporativa:</strong> Dirección y horarios de atención
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>SEO:</strong> Palabras clave para mejorar tu posicionamiento en buscadores
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Hashtags:</strong> Hashtags relevantes para tus campañas en redes sociales
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo configurar:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en <strong>"Gestionar Marketing"</strong> en la tarjeta de tu marca</li>
                                                <li>Completa los campos que desees configurar</li>
                                                <li>Para URLs, puedes escribir solo el dominio (ej: "facebook.com/tupagina") y se añadirá https:// automáticamente</li>
                                                <li>Guarda los cambios</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* Eliminar Marca */}
                                <div className="bg-white border border-red-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                                        8. Eliminar una Marca
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                ⚠️ Advertencia
                                            </h4>
                                            <p className="text-red-700 text-sm mb-2">
                                                Eliminar una marca es una acción <strong>irreversible</strong>. Se eliminarán todos los datos asociados:
                                            </p>
                                            <ul className="text-red-700 text-sm space-y-1 list-disc list-inside">
                                                <li>La marca y toda su configuración</li>
                                                <li>Las tiendas asociadas</li>
                                                <li>Los clientes y sus puntos</li>
                                                <li>El historial de ventas</li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2">Cómo eliminar:</h4>
                                            <ol className="text-yellow-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en el icono de <strong>papelera</strong> en la tarjeta de la marca</li>
                                                <li>Confirma la eliminación en el diálogo que aparece</li>
                                                <li>La marca será eliminada permanentemente</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Gestión de Tiendas */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('tiendas')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faStore} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Gestión de Tiendas</h2>
                                <p className="text-sm text-gray-500">Administra tus ubicaciones físicas</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.tiendas ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.tiendas && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Crear Tienda */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faPlus} className="text-green-600" />
                                        1. Crear una Tienda
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">📍 ¿Dónde crear una tienda?</h4>
                                            <p className="text-green-700 text-sm mb-3">
                                                En la sección de <strong>"Mis Tiendas"</strong>, haz clic en el botón <strong>"Crear Tienda"</strong>.
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-700">Información requerida:</h4>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Nombre:</strong> El nombre de tu tienda (ej: "Tienda Centro", "Sucursal Norte")
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Descripción:</strong> Una breve descripción de la tienda
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Marca asociada:</strong> Selecciona a qué marca pertenece esta tienda
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Dirección completa:</strong> Vía, calle, número, población, región y país
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Puedes crear múltiples tiendas para la misma marca. Esto es útil si tienes varias ubicaciones físicas.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Ver Clientes */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faUsers} className="text-green-600" />
                                        2. Ver Clientes de una Tienda
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Cada tienda tiene su propia lista de clientes registrados. Puedes ver todos los clientes asociados a una tienda específica.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo ver clientes:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la tarjeta de la tienda, haz clic en <strong>"Ver Clientes"</strong></li>
                                                <li>Se abrirá una página con todos los clientes registrados en esa tienda</li>
                                                <li>Puedes buscar, filtrar y ver detalles de cada cliente</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* Crear Cliente */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faUserPlus} className="text-green-600" />
                                        3. Crear Cliente Manualmente
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes registrar clientes manualmente en una tienda. Esto es útil para clientes que no se registraron mediante QR.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo crear un cliente:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la tarjeta de la tienda, haz clic en <strong>"Crear Cliente"</strong></li>
                                                <li>Completa el formulario con los datos del cliente</li>
                                                <li>El cliente será registrado automáticamente en esa tienda</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* Añadir Trabajador/Franquiciado */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faUserPlus} className="text-green-600" />
                                        4. Añadir Trabajador o Franquiciado
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes dar acceso a trabajadores o franquiciados para que gestionen clientes en una tienda específica.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Tipos de acceso:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Trabajador:</strong> Acceso limitado para gestionar clientes en una tienda específica
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Franquiciado:</strong> Acceso para gestionar múltiples tiendas de una franquicia
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo añadir:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en <strong>"Añadir Trabajador/Franquiciado"</strong> en la tarjeta de la tienda</li>
                                                <li>Selecciona si quieres añadir un trabajador o un franquiciado</li>
                                                <li>Completa el formulario con los datos del usuario</li>
                                                <li>El usuario recibirá credenciales para acceder al sistema</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* QR/PDF */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faQrcode} className="text-green-600" />
                                        5. Generar QR y PDF
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Cada tienda tiene su propio código QR único que los clientes pueden escanear para registrarse.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo generar QR/PDF:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en <strong>"QR/PDF"</strong> en la tarjeta de la tienda</li>
                                                <li>Se abrirá un modal con el código QR de la tienda</li>
                                                <li>Puedes descargar el QR en formato PDF para imprimirlo</li>
                                                <li>Coloca el QR impreso en un lugar visible de tu tienda</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Puedes generar múltiples QRs para diferentes ubicaciones dentro de la misma tienda (ej: en la caja, en la entrada, etc.)
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* TPV */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faCreditCard} className="text-green-600" />
                                        6. Configurar Terminal de Punto de Venta (TPV)
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            El TPV permite que los puntos se acumulen automáticamente cuando se realiza una venta en tu terminal.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Configuración inicial:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en <strong>"TPV"</strong> en la tarjeta de la tienda</li>
                                                <li>Si es la primera vez, haz clic en <strong>"Configurar TPV"</strong></li>
                                                <li>Completa la información del terminal (nombre, tipo de software, IP, etc.)</li>
                                                <li>Recibirás una API Key que deberás configurar en tu software Agora</li>
                                                <li>Una vez configurado, los puntos se acumularán automáticamente</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Gestionar TPV:</h4>
                                            <p className="text-green-700 text-sm">
                                                Una vez configurado, puedes hacer clic en <strong>"TPV"</strong> para gestionar la configuración, 
                                                ver estadísticas de transacciones y modificar la configuración.
                                            </p>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-yellow-700 text-sm">
                                                <strong>Nota:</strong> Para más información sobre la configuración del TPV Agora, 
                                                consulta la <Link to="/documentation/integraciones/tpv-agora" className="underline font-medium">guía de integración con TPV Agora</Link>.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Google My Business */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faStar} className="text-green-600" />
                                        7. Configurar Google My Business
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Vincula tu tienda con Google My Business para facilitar que los clientes encuentren tu negocio y dejen reseñas.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo configurar:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en <strong>"Google Reviews"</strong> en la tarjeta de la tienda</li>
                                                <li>Obtén el enlace de "Pedir reseña" desde tu perfil de Google Business</li>
                                                <li>Pega el enlace en el campo del formulario</li>
                                                <li>Guarda la configuración</li>
                                            </ol>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo obtener el enlace:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Entra en <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="underline">Google Business Profile</a></li>
                                                <li>Haz clic en <strong>"Ver tu perfil"</strong></li>
                                                <li>En la nueva página, haz clic en <strong>"Pedir una reseña"</strong></li>
                                                <li>Copia el enlace del modal (formato: <code className="text-xs bg-blue-100 px-1 rounded">https://g.page/r/...</code>)</li>
                                                <li>Pégalo en el campo del formulario</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* Eliminar Tienda */}
                                <div className="bg-white border border-red-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faTrash} className="text-red-600" />
                                        8. Eliminar una Tienda
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                ⚠️ Advertencia
                                            </h4>
                                            <p className="text-red-700 text-sm mb-2">
                                                Eliminar una tienda es una acción <strong>irreversible</strong>. Se eliminarán:
                                            </p>
                                            <ul className="text-red-700 text-sm space-y-1 list-disc list-inside">
                                                <li>La tienda y toda su configuración</li>
                                                <li>Los clientes asociados a esa tienda</li>
                                                <li>El historial de ventas de la tienda</li>
                                                <li>Las configuraciones de TPV</li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2">Cómo eliminar:</h4>
                                            <ol className="text-yellow-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Haz clic en el icono de <strong>papelera</strong> en la tarjeta de la tienda</li>
                                                <li>Confirma la eliminación en el diálogo que aparece</li>
                                                <li>La tienda será eliminada permanentemente</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Herramientas Adicionales */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('herramientas')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faCode} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Herramientas Adicionales</h2>
                                <p className="text-sm text-gray-500">Formulario embebible y búsqueda de tarjetas</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.herramientas ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.herramientas && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Formulario Embebible */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faGlobe} className="text-orange-600" />
                                        1. Formulario Embebible en tu Web
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes agregar un formulario de registro directamente en tu sitio web. Los clientes podrán seleccionar su tienda 
                                            de consumo habitual y registrarse sin salir de tu página.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo configurar:</h4>
                                            <ol className="text-orange-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la sección de "Mis Marcas", haz clic en <strong>"Añadir Formulario en Web Propia"</strong></li>
                                                <li>Se abrirá un modal con opciones de personalización</li>
                                                <li>Configura los estilos (colores, fuentes, etc.)</li>
                                                <li>Copia el código que se genera</li>
                                                <li>Pega el código en tu sitio web donde quieras que aparezca el formulario</li>
                                            </ol>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Tipos de código:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Código Script (Recomendado):</strong> Más flexible y permite personalización avanzada
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Código Iframe:</strong> Más simple pero menos personalizable
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Buscar Tarjeta Pública */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faQrcode} className="text-orange-600" />
                                        2. Página Pública de Búsqueda de Tarjeta
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Los clientes pueden buscar su tarjeta de puntos usando su email o teléfono desde una página pública.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo acceder:</h4>
                                            <p className="text-orange-700 text-sm mb-2">
                                                Haz clic en <strong>"Buscar Tarjeta (Pública)"</strong> en la tarjeta de tu marca. 
                                                Esto te llevará a la página pública donde los clientes pueden buscar su tarjeta.
                                            </p>
                                            <p className="text-orange-700 text-sm">
                                                Puedes compartir el enlace de esta página con tus clientes para que puedan acceder fácilmente a su tarjeta digital.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <Link
                        to="/documentation/gestion"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Volver a Gestión
                    </Link>
                    <div className="text-sm text-gray-500">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                        Guía completa
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default GestionMarcasTiendas;

